<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :min-width="xs ? '100vw' : 260"
    :max-width="xs ? '100vw' : 350"
    :location="xs ? undefined : 'bottom end'"
    :offset="xs ? 0 : [4, 60]"
    :max-height="xs ? '100dvh' : '600px'"
    :content-class="xs ? 'global-notifications-menu--mobile' : undefined"
  >
    <template v-slot:activator="{ props }">
      <v-btn
        v-bind="props"
        v-if="$auth.isAuthenticated"
        v-show="haveNotes"
        variant="text"
        class="bell-button"
        data-cy="globalAlertBellButton"
      >
        <v-badge color="accent" top-right>
          <template v-slot:badge>
            <span>{{ notificationsNum }}</span>
          </template>
          <v-icon size="x-large">mdi-bell</v-icon>
        </v-badge>
      </v-btn>
    </template>
    <v-card :height="xs ? '100dvh' : 'unset'" :class="{ 'card--mobile': xs }">
      <template v-if="xs">
        <v-row no-gutters>
          <v-card-subtitle>
            {{ $t('alertHistory.notificationTitle') }}
          </v-card-subtitle>
          <v-spacer />
          <v-icon @click="menu = false" class="ma-1">mdi-close</v-icon>
        </v-row>
      </template>
      <template v-else>
        <v-card-subtitle class="btn-subtitle d-flex">
          <div>{{ $t('alertHistory.notificationTitle') }}</div>
          <v-spacer />
          <v-btn
            variant="text"
            class="text-primary px-3 mr-1"
            data-cy="globalAlertViewAll"
            @click="viewAll"
          >
            {{ $t('alertHistory.viewAll') }}
          </v-btn>
        </v-card-subtitle>
      </template>
      <v-list>
        <v-list-item
          v-for="(item, index) in notifications"
          :key="index"
          lines="three"
          class="px-4 py-3"
        >
          <v-divider v-if="index > 0" class="notification-divider" />
          <v-row no-gutters>
            <v-col>
              <v-list-item-title>{{ item.trigger.title }}</v-list-item-title>
            </v-col>
            <v-spacer />
            <v-col>
              <v-list-item-title class="anchor-right text-capitalize">
                {{ item.trigger.severity }}
              </v-list-item-title>
            </v-col>
          </v-row>
          <v-row class="py-3 ma-0">
            <v-col cols="6" class="pa-0">
              <v-list-item-subtitle>
                {{ item.device.name }}
              </v-list-item-subtitle>
            </v-col>
            <v-spacer />
            <v-col cols="6" class="pa-0">
              <v-list-item-subtitle class="anchor-right">
                {{ new Date(item.triggeredAt).toLocaleDateString() }}
                {{ new Date(item.triggeredAt).toLocaleTimeString() }}
              </v-list-item-subtitle>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="8">
              <div class="text-title-small">
                {{ item.message }}
              </div>
            </v-col>
            <v-spacer />
            <v-col>
              <v-btn
                class="clear-button"
                @click="clear(item.id)"
                :loading="buttonLoading[`${item.id}`]"
                :disabled="clearingInProgress"
                variant="outlined"
              >
                {{ $t('buttons.clear') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-list-item>
      </v-list>
      <v-card-actions class="stick-bottom">
        <v-spacer />
        <v-btn
          class="clear-all-button bg-black text-white"
          :loading="clearingAll"
          :disabled="clearingInProgress"
          @click="clearAll"
          variant="text"
        >
          {{ $t('buttons.clearAll') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useDisplay } from 'vuetify'
import type { Notification } from '@/types/alerts'
import { NAMESPACES } from '@/store'
import { ALERTS_ACTIONS } from '@/store/alerts'

export default defineComponent({
  setup() {
    const { xs } = useDisplay()
    return { xs }
  },
  mounted() {
    this.getNotifications()
    this.int = setInterval(this.getNotifications, 60000)
  },
  unmounted() {
    if (this.int) {
      clearInterval(this.int)
    }
  },
  data() {
    return {
      menu: false,
      int: null as ReturnType<typeof setInterval> | null,
      buttonLoading: {} as Record<string, boolean>,
      clearingInProgress: false,
      clearingAll: false
    }
  },
  computed: {
    notificationsNum(): number | string {
      const n = this.$store.getters['alerts/getNotificationsNum']
      return n > 8 ? '9+' : n
    },
    notifications(): Notification[] {
      const n: Notification[] = this.$store.getters['alerts/getNotifications']
      if (!n) return n
      return n.slice().sort((a: Notification, b: Notification) => {
        const ad = Date.parse(a.triggeredAt)
        const bd = Date.parse(b.triggeredAt)
        if (ad < bd) return 1
        if (ad > bd) return -1
        return 0
      })
    },
    haveNotes(): boolean {
      return (
        (typeof this.notificationsNum === 'number' && this.notificationsNum > 0) ||
        this.notificationsNum === '9+'
      )
    }
  },
  methods: {
    async getNotifications(): Promise<void> {
      const res = await this.$api.getAlertNotifications({
        isRead: false,
        limit: 25
      })
      if (res.ok) {
        const body = await res.json()
        this.$store.dispatch(`${NAMESPACES.ALERTS}/${ALERTS_ACTIONS.SET_NOTIFICATIONS}`, body)
        if (typeof this.notificationsNum === 'number' && this.notificationsNum < 1) {
          this.menu = false
        }
        this.clearingInProgress = false
        this.clearingAll = false
      }
    },
    async clear(id: string): Promise<void> {
      this.clearingInProgress = true
      this.buttonLoading = { [`${id}`]: true }
      await this.$api.patchClearNotification(id)
      await this.getNotifications()
    },
    async clearAll(): Promise<void> {
      this.clearingInProgress = true
      this.clearingAll = true
      await this.$api.postClearAllNotifications()
      await this.getNotifications()
    },
    viewAll(): void {
      this.menu = false
      const route = 'alert-history'
      if (this.$route.name !== route) this.$router.push({ name: route })
    }
  }
})
</script>

<style lang="scss" scoped>
.row {
  flex: 1 0 100%;
}

.bell-button {
  height: 100%;
  margin-right: 16px;
}

.card--mobile {
  border-radius: unset;
  width: 100%;
  height: 100%;
}

.notification-divider {
  margin-bottom: 20px;
}

.text-title-small {
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 550;
  letter-spacing: 0.00625em;
  color: var(--tsi-color--dark-56);
}

.anchor-right {
  text-align: end;
}

.stick-bottom {
  position: sticky;
  bottom: 0;
  width: 100%;
  background: white;
}

:deep(.global-notifications-menu--mobile) {
  top: 0 !important;
  left: 0 !important;
  width: 100vw !important;
  max-width: 100vw !important;
  height: 100dvh !important;
  max-height: 100dvh !important;
  margin: 0 !important;
  border-radius: unset !important;
}

.v-btn.clear-button {
  min-width: 72px !important;
  height: 32px !important;
}

.v-btn.clear-all-button {
  width: 104px;
  min-width: 104px !important;
  height: 32px !important;
}

.v-card-subtitle {
  padding: 16px 0px 0px 16px;
  &.btn-subtitle {
    display: flex;
    align-items: center;
  }
}
</style>
